<script setup lang="ts">
import { isEnumValue } from '@/utils/enumUtils';
import { logger } from '@/utils/logger';
import { useGaiaToast } from '@/composables/useGaiaToast';
import { computed, ref, watch } from 'vue';
import { getVaultStatistics, VaultFileRevisionSource, type VaultStatistics } from '@/api';
import prettyBytes from 'pretty-bytes';
import StatisticsBarChartCard from '@/components/admin/StatisticsBarChartCard.vue';
import StatisticsStackedBarChartCard, {
  type StackedSeries,
} from '@/components/admin/StatisticsStackedBarChartCard.vue';
import { vaultFileRevisionSourceMap } from '@/components/design/statusMaps';
import StatisticsKpiGrid, {
  type StatisticsKpiTile,
} from '@/components/admin/StatisticsKpiGrid.vue';
import {
  buildStatisticsDateQuery,
  formatFileTypeLabel,
  formatStatisticsNumber,
  sortStatisticsEntries,
  type StatisticsFiltersProps,
} from '@/utils/adminStatistics';
import { categoricalColor } from '@/utils/usageColors';

const props = defineProps<StatisticsFiltersProps>();
const message = useGaiaToast();

const data = ref<VaultStatistics | undefined>(undefined);
const loading = ref(false);

const kpiTiles = computed<StatisticsKpiTile[]>(() => {
  if (data.value === undefined) {
    return [];
  }

  return [
    { label: 'Total files', value: formatStatisticsNumber(data.value.total_files) },
    { label: 'Failed previews', value: formatStatisticsNumber(data.value.failed_previews) },
    { label: 'Failed OCRs', value: formatStatisticsNumber(data.value.failed_ocrs) },
    {
      label: 'Avg files / org',
      value: formatStatisticsNumber(data.value.average_files_per_organisation),
    },
    {
      label: 'Avg directories / org',
      value: formatStatisticsNumber(data.value.average_directories_per_organisation),
    },
    {
      label: 'Avg files / directory',
      value: formatStatisticsNumber(data.value.average_files_per_directory),
    },
    {
      label: 'Avg revisions / file',
      value: formatStatisticsNumber(data.value.average_revisions_per_file),
    },
  ];
});

const fileTypeCounts = computed(() =>
  sortStatisticsEntries(data.value?.file_type_counts, 'value-desc')
);
const averageFileTypeSizes = computed(() =>
  sortStatisticsEntries(data.value?.average_file_type_sizes, 'value-desc')
);
const filesBySource = computed(() =>
  sortStatisticsEntries(data.value?.files_by_source, 'value-desc')
);
const filesBySourcePerDay = computed<{ days: string[]; series: StackedSeries[] }>(() => {
  const perDay = data.value?.files_by_source_per_day ?? {};
  const days = Object.keys(perDay).sort();

  const totals = new Map<VaultFileRevisionSource, number>();
  for (const counts of Object.values(perDay)) {
    for (const [source, count] of Object.entries(counts)) {
      if (!isEnumValue(VaultFileRevisionSource, source)) continue;
      totals.set(source, (totals.get(source) ?? 0) + count);
    }
  }

  // Up to 15 sources, more than the 6-slot default palette, so colour by sorted position.
  const series = Array.from(totals.entries())
    .sort(([, left], [, right]) => right - left)
    .map(([source], index) => ({
      name: formatSourceLabel(source),
      values: days.map(day => perDay[day][source] ?? 0),
      color: categoricalColor(index),
    }));

  return { days, series };
});
const pagesPerFile = computed(() => sortStatisticsEntries(data.value?.pages_per_file, 'key-asc'));

function formatSourceLabel(source: string): string {
  return isEnumValue(VaultFileRevisionSource, source) ? vaultFileRevisionSourceMap[source] : source;
}

function formatBytes(value: number): string {
  return prettyBytes(value);
}

async function fetchStatistics(): Promise<void> {
  loading.value = true;
  try {
    const response = await getVaultStatistics({
      query: {
        included_org_uids: props.includedOrgUids,
        excluded_org_uids: props.excludedOrgUids,
        ...buildStatisticsDateQuery(props.dateRange),
      },
    });
    if (response.response?.status === 200) {
      data.value = response.data;
    }
  } catch (error) {
    message.error('Failed to load vault statistics');
    logger.error('Failed to load vault statistics:', error);
  } finally {
    loading.value = false;
  }
}

watch(props, fetchStatistics, { deep: true, immediate: true });
</script>

<template>
  <n-spin :show="loading">
    <n-flex v-if="data !== undefined" vertical :size="16">
      <statistics-kpi-grid :tiles="kpiTiles" />

      <n-grid cols="1 s:1 m:2 xl:3" responsive="screen" :x-gap="16" :y-gap="16">
        <n-gi>
          <statistics-bar-chart-card
            title="Pages per file"
            :rows="pagesPerFile"
            x-label="Pages"
            y-label="Files"
          />
        </n-gi>
        <n-gi>
          <statistics-bar-chart-card
            title="File type counts"
            :rows="fileTypeCounts"
            x-label="Files"
            y-label="File type"
            horizontal
            :format-category="formatFileTypeLabel"
          />
        </n-gi>
        <n-gi>
          <statistics-bar-chart-card
            title="Average file size by type"
            :rows="averageFileTypeSizes"
            x-label="Average size"
            y-label="File type"
            horizontal
            :format-category="formatFileTypeLabel"
            :format-value="formatBytes"
          />
        </n-gi>
        <n-gi>
          <statistics-bar-chart-card
            title="Files by source"
            :rows="filesBySource"
            x-label="Files"
            y-label="Source"
            horizontal
            :format-category="formatSourceLabel"
          />
        </n-gi>
      </n-grid>

      <statistics-stacked-bar-chart-card
        title="Files by source per day"
        :categories="filesBySourcePerDay.days"
        :series="filesBySourcePerDay.series"
        x-label="Day"
        y-label="Files"
        empty-description="No files in the selected range"
      />
    </n-flex>
    <div v-else style="min-height: 200px" />
  </n-spin>
</template>

<style scoped></style>
